import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Slider,
  Tooltip,
  Fab,
  Fade,
  Alert,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Lottie from "react-lottie";
import uploadAnimationData from "../animations/resume-parsing.json";
import resultAnimationData from "../animations/0.json";
import apis from "../../utils/apis";
import { getEmail } from "../../utils/storage";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { StepIconProps } from "@mui/material/StepIcon";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MoodIcon from "@mui/icons-material/Mood";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import { debounce } from "lodash";
import NumberQuestionComponent from "../stepper_form/question-type-components/NumberQuestionComponent";
import ExperienceInput from "./ExperienceInput";

// Custom styled components
const UploadContainer = styled(Paper)(({ theme, hasFile }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  border: "2px dashed #ccc",
  borderRadius: "12px",
  backgroundColor: hasFile ? "#e8f5e9" : "#fafafa",
  transition: "all 0.3s ease",
  overflow: "hidden",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#f0f4ff",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "28px",
  padding: "8px 30px",
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "#0D9B21",
  "&:hover": {
    backgroundColor: "#35b848",
  },
  color: "#fff",
}));
const StyledButtonContinue = styled(Button)(({ theme }) => ({
  borderRadius: "28px",
  padding: "8px 30px",
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "#26801E",
  "&:hover": {
    backgroundColor: "#35b848",
  },
  color: "#fff",
}));

const ResultCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "12px",
  padding: theme.spacing(3),
}));
  
const ErrorText = styled(Typography)(({ theme }) => ({
  color: "#F44336",
  textAlign: "center",
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
}));

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  "& .MuiStepConnector-line": {
    borderColor: "#4A3D9E",
    borderWidth: 2,
    borderRadius: 1,
  },
}));

const CustomStepIcon = styled("div")(({ theme, active, completed }) => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  backgroundColor: completed ? "#4CAF50" : active ? "#4A3D9E" : "#bdbdbd",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.9rem",
  fontWeight: "bold",
  transition: "all 0.3s ease",
}));

const UploadResume = ({
  DataDecryption,
  getUserform,
  setShowResumeUpload,
  setResumeUpload,
  dispatch,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingResult, setIsFetchingResult] = useState(false);
  const [error, setError] = useState(null);
  const [convertedMarketSalaryRange, setConvertedMarketSalaryRange] =
    useState(null);
  const [updatedExperience, setUpdatedExperience] = useState(0); // Keep for compatibility
  const [updatedExperienceMonths, setUpdatedExperienceMonths] = useState(0); // Experience in months
  const [parsedExperiences, setParsedExperiences] = useState("");
  const [userFormData, setUserFormData] = useState("");
  const [expWarning, setExpWarning] = useState(false);
  const [warning, setWarning] = useState(null); // New state for warnings

  const Upload_Other_Resumes = useFeatureFlag("Upload_Other_Resumes");
  const [highestValueFormdata, setHighestValueFormdata] = useState({});
  const [salaryRange, setSalaryRange] = React.useState({
    higherTechValue: 0,
    lowerTechValue: 0,
    higherMarketSalaryValue: 0,
    lowerMarketSalaryValue: 0,
  });
  const ResumeAcceptedValidityPeriod = useFeatureFlag(
    "Resume_Accepted_Validity_Period"
  );
  const abortControllerRef = useRef(null);
  let cancelTokenSource = null;
  const Admin_Access = useFeatureFlag("Admin_Access");
  const allowedEmails = JSON.parse(Admin_Access);
  const userEmail = getEmail();
  const steps = ["Upload Resume", "Adjust Experience", "Salary Range"];

  // Convert years to months for initial state
  useEffect(() => {
    setUpdatedExperienceMonths(updatedExperience);
  }, [updatedExperience]);

  const isFileRecent = (file) => {
    const currentDate = new Date(); // Current system date
    const monthsAgo = new Date(currentDate);

    // Use the harness flag value, defaulting to 6 if the flag is not available
    const validityPeriod = ResumeAcceptedValidityPeriod || 6;
    monthsAgo.setMonth(currentDate.getMonth() - validityPeriod);

    const fileModifiedDate = new Date(file.lastModified);
    return fileModifiedDate >= monthsAgo;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      const validityPeriod = ResumeAcceptedValidityPeriod || 6;

      if (!["pdf"].includes(fileExtension)) {
        setError("Only .pdf files are allowed.");
        setFile(null);
      } else if (fileSizeMB > 2) {
        setError("File size must be smaller than 2 MB.");
        setFile(null);
      } else {
        if (!isFileRecent(selectedFile)) {
          setWarning(
            `Warning: Your resume is older than ${validityPeriod} months. Results may not be accurate.`
          );
        } else {
          setWarning(null);
        }
        setFile(selectedFile);
        setError(null);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const selectedFile = e.dataTransfer.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      const validityPeriod = ResumeAcceptedValidityPeriod || 6;

      if (!["pdf"].includes(fileExtension)) {
        setError("Only .pdf files are allowed.");
        setFile(null);
      } else if (fileSizeMB > 2) {
        setError("File size must be smaller than 2 MB.");
        setFile(null);
      } else {
        if (!isFileRecent(selectedFile)) {
          setWarning(
            `Warning: Your resume is older than ${validityPeriod} months. Results may not be accurate.`
          );
        } else {
          setWarning(null);
        }
        setFile(selectedFile);
        setError(null);
      }
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const convertExperienceToYearsAndMonths = (experience) => {
    if (!experience) return { years: 1, months: 0 };
    const regex = /(\d+)\s*(\+?\s*years?)/i;
    const match = experience.match(regex);
    if (match) return { years: parseInt(match[1], 10), months: 0 };
    const moreThanRegex = /more than\s+(\d+)\s*years?/i;
    const moreThanMatch = experience.match(moreThanRegex);
    if (moreThanMatch)
      return { years: parseInt(moreThanMatch[1], 10), months: 0 };
    return { years: 1, months: 0 };
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file.");
      return;
    }

    setIsUploading(true);
    setError(null);
    const reader = new FileReader();

    reader.onloadend = async () => {
      try {
        const fileContent = reader.result.split(",")[1];
        if (cancelTokenSource)
          cancelTokenSource.cancel("Previous request cancelled.");
        cancelTokenSource = axios.CancelToken.source();

        let retries = 2; // Number of retries
        let response;

        while (retries >= 0) {
          try {
            response = await apis.POST({
              endpoint: "/get-parse-resume",
              payload: { fileName: file.name, fileContent },
              options: { type: "json" },
            });

            if (response.data.error) {
              throw new Error("Resume parsing failed");
            }
            break; // Exit loop if successful
          } catch (error) {
            if (error.response?.status === 504 && retries > 0) {
              retries -= 1; // Decrement retries and retry
            } else {
              throw error; // Rethrow if not a 504 or no retries left
            }
          }
        }

        const decryptionData = DataDecryption(response.data);
        const parsedData = JSON.parse(decryptionData.body);
        const email = getEmail();
        let parsedResume = JSON.parse(parsedData.openAiResponse);
        if (parsedResume.email !== email && !Upload_Other_Resumes) {
          setError("Please upload your own resume");
          throw new Error("Email mismatch");
        }

        if (
          !parsedResume.name ||
          !parsedResume.email ||
          !parsedResume.primarySkill?.length
        ) {
          throw new Error("Resume is missing required information");
        }

        const updatedUserFormData = await apis.POST({
          endpoint: "/get-updated-form",
          payload: parsedResume,
          options: { type: "json", cancelToken: cancelTokenSource.token },
        });

        if (!updatedUserFormData.data?.userForm) {
          throw new Error("Failed to process resume data");
        }

        const userForm = updatedUserFormData.data.userForm;
        const highestValueFormdata = updatedUserFormData.data.userFormHighest;
        setHighestValueFormdata({
          ...highestValueFormdata,
          higherValueForm: true,
        });
        setUserFormData({ ...userForm, payloadType: "resume-parsed" });

        if (userForm.experience) {
          const months = parseInt(userForm.experience, 10) || 12;       

          setUpdatedExperience(months); // Set years separately
          setUpdatedExperienceMonths(months); // Store total months
          setParsedExperiences(formatExperience(months)); // Convert to readable format

          userForm.experience = `${months}`; // Store as months
          userForm.payloadType = "resume-parsed";
        }

        setActiveStep(1);
      } catch (error) {
        setError(
          error.message === "Resume parsing failed"
            ? "Resume parsing service is currently unavailable. Please try again later."
            : error.message === "Email mismatch"
            ? "Please upload your own resume"
            : error.message === "Resume is missing required information"
            ? "Your resume is missing key details (name, email, or skills). Please upload a complete resume."
            : error.message === "Failed to process resume data"
            ? "We couldn't process your resume data. Please try again later."
            : "Something went wrong while processing your resume. Please try again."
        );
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = () => {
      setError("Unable to read your resume file. Please try a different file.");
      setIsUploading(false);
    };

    reader.readAsDataURL(file);
  };

  const handleExperienceChange = (e, newValue) => {
    setUpdatedExperienceMonths(newValue);
  };

  const handleCheckMarketSalary = async () => {
    try {
      setIsFetchingResult(true);
      setError(null);

      // First API call to get higherTechValue
      const higherValueResult = await apis.POST({
        endpoint: "/get-result",
        payload: highestValueFormdata,
        options: { type: "json" },
      });

      if (higherValueResult.data.error) {
        throw new Error("Failed to fetch higher tech value");
      }

      setSalaryRange((prev) => ({
        ...prev,
        higherTechValue: higherValueResult.data.convertedTechValueHigherRange,
        higherMarketSalaryValue:
          higherValueResult.data.convertedMarketSalaryHigherRange,
      }));

      // Second API call for actual values
      const updatedParsedResume = {
        ...userFormData,
        experience: `${updatedExperienceMonths}`,
        payloadType: "resume-parsed",
      };

      const resultResponse = await apis.POST({
        endpoint: "/get-result",
        payload: updatedParsedResume,
        options: { type: "json" },
      });

      if (resultResponse.data.error) {
        throw new Error("Salary calculation failed");
      }

      if (!resultResponse.data.convertedMarketSalaryRange) {
        throw new Error("No salary data available");
      }

      const resultData = resultResponse.data;
      setConvertedMarketSalaryRange(resultData.convertedMarketSalaryRange);
      setSalaryRange((prev) => ({
        ...prev,
        lowerTechValue: resultData.convertedTechValue,
        lowerMarketSalaryValue: resultData.convertedMarketSalary,
      }));

      getUserform();
      setActiveStep(2);
    } catch (error) {
      setError(
        error.message === "Failed to fetch higher tech value"
          ? "We couldn't fetch the higher tech value. Please try again later."
          : error.message === "Salary calculation failed"
          ? "We couldn't calculate your market salary at this time. Please try again later."
          : error.message === "No salary data available"
          ? "We don't have enough data to calculate your salary range. Please try again later."
          : "Something went wrong while calculating your salary range. Please try again."
      );
    } finally {
      setIsFetchingResult(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setConvertedMarketSalaryRange(null);
    setParsedExperiences("");
    setUpdatedExperience(0);
    setUpdatedExperienceMonths(0);
    setUserFormData("");
    setError(null);
  };

  const formatExperience = (months) => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    let result = "";
    if (years > 0) {
      result += `${years} ${years === 1 ? "year" : "years"}`;
    }
    if (remainingMonths > 0) {
      result += `${years > 0 ? " " : ""}${remainingMonths} ${
        remainingMonths === 1 ? "month" : "months"
      }`;
    }
    return result || "0 months";
  };

  const getExperienceEmoji = (months) => {
    const years = Math.floor(months / 12);
    if (years >= 10)
      return <EmojiEventsIcon sx={{ color: "#FFD700", ml: 1 }} />;
    if (years >= 5) return <MoodIcon sx={{ color: "#4CAF50", ml: 1 }} />;
    return <SentimentSatisfiedIcon sx={{ color: "#3f51b5", ml: 1 }} />;
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <UploadContainer elevation={2} hasFile={!!file}>
              <Box
                sx={{
                  textAlign: "center",
                  minHeight: 120,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                
                {file ? (
                <Typography
                  variant="body1"
                  sx={{
                    color: "#000000",
                    fontWeight: 500,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    maxWidth: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    px: 1,
                  }}
                >
                  <CloudUploadIcon
                    sx={{ fontSize: 36, color: "#000000" }}
                  />
                  <span>{file.name}</span>
                </Typography>
              ) : (
                <>
                  <CloudUploadIcon
                    sx={{ fontSize: 40, color: "#4A3D9E", mb: 1 }}
                  />
                  <Typography variant="body1" sx={{ color: "#000000" }}>
                    Drag & drop your resume here
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "#000000" }}
                  >
                    or click to browse (.pdf)
                  </Typography>
                </>
              )}

              </Box>
            </UploadContainer>
            <Box
              sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "center" }}
            >
              <StyledButton
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={isUploading}
                sx={{ borderColor: "#3f51b5", color: "#fff" }}
              >
                Browse
                <input
                  type="file"
                  hidden
                  accept=".pdf,.docx"
                  onChange={handleFileChange}
                />
              </StyledButton>
              <StyledButton
                variant="contained"
                onClick={handleUpload}
                disabled={isUploading || !file}
              >
                {isUploading ? "Uploading..." : "Upload"}
              </StyledButton>
            </Box>
            {warning && (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "orange", mt: 2 }}
              >
                {warning}
              </Typography>
            )}
            {error && <ErrorText>{error}</ErrorText>}
          </Box>
        );
      case 1:
        return parsedExperiences && !isFetchingResult ? (
          <>
            <Box sx={{ px: { xs: 1, sm: 2 } }}>
              <Typography variant="h6" sx={{ mb: 2, color: "#3f51b5" }}>
                Adjust Your Experience
              </Typography>

              {/* Show selected resume file name */}
              {file && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "text.secondary",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  Selected Resume: <strong>{file.name}</strong>
                </Typography>
              )}

              {expWarning && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  Experience parsing might be inaccurate. Please adjust if
                  needed.
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "center",
                  flexDirection: "column",
                  color: "#ffffff",
                }}
              >
                {/* <Slider
                  value={updatedExperienceMonths}
                  onChange={handleExperienceChange}
                  min={0}
                  max={480}
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => formatExperience(value)}
                  sx={{ flexGrow: 1, minWidth: 150, color: "#3f51b5" }}
                /> */}

                {/* Number Input Component for Experience */}
                <ExperienceInput
                  value={updatedExperienceMonths}
                  onChange={setUpdatedExperienceMonths}
                />
                {/* <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {formatExperience(updatedExperienceMonths)}
                </Typography> */}
              </Box>

              <Box
                sx={{
                  textAlign: "center",
                  gap: 2,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <StyledButton
                  variant="outlined"
                  onClick={() => setActiveStep(activeStep - 1)}
                  sx={{ mt: 3 }}
                >
                  Back to Upload
                </StyledButton>
                <StyledButton
                  variant="contained"
                  onClick={handleCheckMarketSalary}
                  sx={{ mt: 3 }}
                >
                  Get Market Salary
                </StyledButton>
              </Box>
              {warning && (
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", color: "orange", mt: 2 }}
                >
                  {warning}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <>
            <Lottie
              options={{
                animationData: resultAnimationData,
                loop: true,
                autoplay: true,
              }}
              height={300}
              width={300}
            />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Calculating Salary Range...
            </Typography>
          </>
        );

      case 2:
        return (
          <>
            <ResultCard>
              {file && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  Selected Resume: <strong>{file.name}</strong>
                </Typography>
              )}

              <CheckCircleIcon sx={{ fontSize: 60, color: "#4CAF50", mb: 1 }} />
              <Typography variant="h6" sx={{ color: "#4CAF50", mb: 1 }}>
                Your Market Salary Range
              </Typography>
              <Typography
                variant="h3"
                sx={{ color: "#4CAF50", fontWeight: 700, mb: 1 }}
              >
                {convertedMarketSalaryRange?.[0]} -
                {convertedMarketSalaryRange?.[1]}
              </Typography>
              {allowedEmails.includes(userEmail) && (
                <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Typography variant="h6" sx={{ color: "#3f51b5", mb: 1 }}>
                    Salary Range
                  </Typography>
                  <Typography>MarketSalary:</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {salaryRange.lowerMarketSalaryValue} -{" "}
                    {salaryRange.higherMarketSalaryValue}
                  </Typography>
                  <Typography>TechValue:</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0 }}>
                    {salaryRange.lowerTechValue} - {salaryRange.higherTechValue}
                  </Typography>
                </Box>
              )}
              <Fab
                color="primary"
                size="medium"
                onClick={handleReset}
                sx={{
                  position: "fixed",
                  bottom: { xs: 46, sm: 46 },
                  right: { xs: 26, sm: 45 },
                  backgroundColor: "#3f51b5",
                }}
              >
                <ArrowBackIcon />
              </Fab>
            </ResultCard>
            {warning && (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "orange", mt: 2 }}
              >
                {warning}
              </Typography>
            )}
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" color="warning.main" sx={{ mb: 0 }}>
                Resume parsing may contain errors. Please validate your
                information to ensure accuracy.
              </Typography>
            </Box>
          </>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pt: { xs: 2, md: 4, xl: 4 },
        pb: { xs: 2, md: 4, xl: 4 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 600 },
          mx: "auto",
          px: { xs: 2, sm: 3 },
          bgcolor: "white",
          borderRadius: "16px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
          py: 4,
          position: "relative",
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<CustomConnector />}
          sx={{ mb: 5 }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <CustomStepIcon active={active} completed={completed}>
                    {completed ? "✓" : index + 1}
                  </CustomStepIcon>
                )}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    color: activeStep >= index ? "#3f51b5" : "#757575",
                    fontWeight: activeStep === index ? 600 : 400,
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box>{getStepContent(activeStep)}</Box>
        {isUploading === false && isFetchingResult === false && (
          <Box>
            {/* Text Section */}
            <Typography
              variant="body1"
              sx={{
                mt: { xs: 2, sm: 3 }, // Spacing above text
                mb: { xs: 1, sm: 2 }, // Spacing below text before button
                fontSize: { xs: "0.9rem", md: "1rem" }, // Adaptive font size
                textAlign: "center",
                color: "text.secondary",
              }}  
            >
              Validate your info and add missing fields
            </Typography>

            {/* Button Section */}
            <StyledButton
              onClick={() => {
                setShowResumeUpload(false);
                dispatch(setResumeUpload(false));
              }}
            >
              {activeStep === 0 || activeStep === 1 ? "Cancel" : "Close"}
            </StyledButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UploadResume;  
